/* eslint-disable no-param-reassign, no-underscore-dangle, no-console */
import axios from 'axios';
import { getLocale } from '@/helpers/Utils';
import { parseKomodoJson } from './bigint';
import {
	KOMODO_API_PREFIX,
	KOMODO_CODE,
	clearSession,
	createKomodoError,
	ensureSession,
	getDeptScope,
	getDeptTimezone,
	isKomodoError,
	unwrapEnvelope,
} from './KomodoAuth';

/**
 * Axios instance dedicated to server.komodo (PSI). Deliberately separate from
 * `@/services/HttpClient`: that client injects the ZiiCloud `Token`,
 * `MerchantId` and `BranchId` headers, and komodo interprets `BranchId` as its
 * own company code (DeptCode). Nothing under src/views/app/psi-config may
 * import HttpClient for komodo calls.
 */
const client = axios.create({
	baseURL: KOMODO_API_PREFIX,
	timeout: 60000,
	// Snowflake ids (PID) exceed 2^53 — see bigint.js.
	transformResponse: [parseKomodoJson],
});

const isUnauthorized = (code) =>
	code === KOMODO_CODE.UNAUTHORIZED || code === KOMODO_CODE.PWD_CHANGED;

const retryWithFreshSession = async (config) => {
	clearSession();
	await ensureSession({ force: true });
	return client.request({ ...config, __komodoRetried: true });
};

client.interceptors.request.use(async (config) => {
	if (
		process.env.NODE_ENV !== 'production' &&
		!/^\/(api|openapi)\//.test(config.url || '')
	) {
		console.warn(
			`>>> KomodoClient: url should start with /api/ (got ${config.url})`
		);
	}
	const session = await ensureSession();
	config.headers = { ...(config.headers || {}) };
	config.headers.Authorization = `Bearer ${session.authToken}`;
	config.headers.MerchantId = session.merchantID;
	// A ZiiCloud branch id here would poison komodo's tenant filter.
	delete config.headers.BranchId;
	const deptCode = getDeptScope();
	if (deptCode) {
		config.headers.BranchId = deptCode;
	}
	const timezone = getDeptTimezone();
	if (timezone) {
		config.headers['X-Timezone'] = timezone;
	}
	config.headers['Accept-Language'] = getLocale() || 'en-AU';
	return config;
});

client.interceptors.response.use(
	async (response) => {
		const body = response.data;
		const isEnvelope =
			body &&
			typeof body === 'object' &&
			(body.code !== undefined || body.Code !== undefined);
		if (!isEnvelope) {
			// Raw payloads (file streams, non-komodo responses) pass through.
			return response;
		}
		try {
			return unwrapEnvelope(body);
		} catch (error) {
			if (isUnauthorized(error.code) && !response.config.__komodoRetried) {
				return retryWithFreshSession(response.config);
			}
			throw error;
		}
	},
	async (error) => {
		const { response, config } = error;
		if (
			response &&
			response.status === 401 &&
			config &&
			!config.__komodoRetried
		) {
			return retryWithFreshSession(config);
		}
		if (isKomodoError(error)) {
			throw error;
		}
		const body = response ? response.data : null;
		const code = body && (body.code || body.Code);
		if (code) {
			throw createKomodoError(
				parseInt(code, 10),
				body.message || body.Message,
				body
			);
		}
		console.error('>>> KomodoClient response error:', error);
		throw createKomodoError(
			response ? response.status : 0,
			error.message || 'PSI request failed',
			body
		);
	}
);

// Same call signatures as @/services/HttpClient (note: `del` takes params before data).
const get = (url, params, options) =>
	client.get(url, { params, ...(options || {}) });
const post = (url, data, params, options) =>
	client.post(url, data, { params, ...(options || {}) });
const put = (url, data, params, options) =>
	client.put(url, data, { params, ...(options || {}) });
const patch = (url, data, params, options) =>
	client.patch(url, data, { params, ...(options || {}) });
const del = (url, params, data, options) =>
	client.delete(url, { params, data, ...(options || {}) });

export default { get, post, put, patch, del, isKomodoError, KOMODO_CODE };
